import {useState, useEffect, useRef, useCallback} from 'react';
import {AppProps} from 'next/app';
import Router, {useRouter} from 'next/router';
import {useRecoilState, useRecoilValue} from 'recoil';
import classNames from 'classnames';
import Layer from '@src/components/layer/Layer';
import AuthInfo from '@src/components/AuthInfo';
import Footer from '@src/components/Footer';
import {layerState, tagFixedState} from '@src/store';
import {isSandbox} from '@src/utils/common';
import {matchPathname} from '@src/utils/route';
import QRpopup from './bridge/QRpopup';
import {TIARA_CUSTOM_PROPS} from '@src/constants/tiara';

const FOLDED_FOOTER_HEIGHT = 135;
const UNFOLDED_FOOTER_HEIGHT = 260;
const ERROR_CLASSNAME = 'wrap_error';
const TAB_FIXED = 'tab_fixed';
const TAG_FIXED = 'tag_fixed';
const MOBILE_BRIDGE_CLASSNAME = 'wrap_empty_mo';
const PAGE_BOTTOM_BTN = 'page_btn';
const CONTAINER_CLOSE = 'container-close';
const AREA_CLOSE = 'area_close';
const errorPageRegexps = [
  /^\/errors/, // pages/errors/** */
  '/404',
];
const homePageRegexps = ['/', '/?tab=1', '/?tab=2'];
const mobileBridgePageRegexps = [
  '/bridge/mobile',
  '/bridge/mobile-update',
  '/bridge/redirect-permalink',
  '/planning/done',
];
const BrandPageRegexps = ['/intro'];
const bottomButtonRegexps = [
  /^\/landing/, // pages/landing/** */
  /^\/event/, // pages/event/** */
];
const ClosePageRegexp = '/close';
const ClosePcPageRegexp = '/close-pc';

// data-tiara-custom=k=v&k=v&k=v
const tiaraCustomProps = Object.entries(TIARA_CUSTOM_PROPS)
  .map(([key, value]) => `${key}=${value}`)
  .join('&');

function AppMain (props: AppProps) {
  const {Component, pageProps} = props;
  const [isFoldedFooter, setIsFoldedFooter] = useState(true);
  const router = useRouter();
  const [deviceHeight, setDeviceHeight] = useState('100vh');
  const [{open: isShowLayer}, setLayer] = useRecoilState(layerState);
  const isTagFixed = useRecoilValue(tagFixedState);

  const rootRef = useRef<HTMLDivElement>(null);

  const isErrorPage = matchPathname(errorPageRegexps, router.pathname);
  const isHomePage = matchPathname(homePageRegexps, router.pathname);
  const isMobileBridgePage = matchPathname(mobileBridgePageRegexps, router.pathname);
  const isBrandPage = matchPathname(BrandPageRegexps, router.pathname);
  const isClosePage = router.pathname === ClosePageRegexp;
  const isClosePcPage = router.pathname === ClosePcPageRegexp;

  const isBottomButtonPage = matchPathname(bottomButtonRegexps, router.pathname);

  useEffect(() => {
    // [Android] 100vh 값을 Keyboard 높이를 제외하는 현상으로 인해 다바이스 높이 값을 따로 저장
    setDeviceHeight(`${window.innerHeight}px`);
  }, []);

  // 레이어가 떠있을 경우, 뒤로가기 및 스크롤 차단
  useEffect(() => {
    Router.beforePopState(() => {
      if (isShowLayer) {
        setLayer({open: false});
        history.go(1);
      }

      return !isShowLayer;
    });

    const hasOpenedLayer = document.querySelectorAll('.isPopupOpen').length > 0;

    window.document.body.style.overflow = isShowLayer || hasOpenedLayer ? 'hidden' : '';
  }, [isShowLayer, setLayer]);

  useEffect(() => {
    if (router.pathname === '/planning/[id]') {
      // 상단 뒤로가기 버튼을 이용해서 해당 페이지 접근 시
      // 스크롤 위치가 변경되지만 eact-lazy-load-image-component 에서 해당 사실을 인지 못함
      // 첫 이미지가 그려지지 않는 이슈 발생
      const scrollY = document.documentElement.scrollTop || document.body.scrollTop;
      window.scrollTo(0, scrollY + 1);
    }

    if (router.pathname === '/intro') {
      const htmlTag = window.document.querySelector('html');

      if (htmlTag) {
        htmlTag.className = 'intro_product';
      }
    }
  }, [router.pathname]);

  const scrollToBottom = useCallback(() => {
    window.scrollTo(0, document.body.scrollHeight);
  }, []);

  return (
    <>
      <div
        className={classNames('container-doc', {
          [PAGE_BOTTOM_BTN]: isBottomButtonPage,
          [CONTAINER_CLOSE]: isClosePage,
        })}
        data-tiara-custom={tiaraCustomProps}
        ref={rootRef}
      >
        <header className="doc-header">
          <h1 className="doc-title screen_out">카카오톡 채널 상품구독 홈</h1>
        </header>
        <main
          className="doc-main"
          style={{
            minHeight: `calc(${deviceHeight} - ${isFoldedFooter ? FOLDED_FOOTER_HEIGHT : UNFOLDED_FOOTER_HEIGHT}px)`,
          }}
        >
          <article
            className={classNames('content-article', {
              [ERROR_CLASSNAME]: isErrorPage,
              [TAB_FIXED]: isHomePage,
              [TAG_FIXED]: isHomePage && isTagFixed,
              [MOBILE_BRIDGE_CLASSNAME]: isMobileBridgePage,
              [AREA_CLOSE]: isClosePcPage,
            })}
          >
            <h2 className="screen_out">상품구독 메인 컨텐츠</h2>
            <Component {...pageProps} rootRef={rootRef} />
            <Layer />
          </article>
        </main>
        {isSandbox() && <AuthInfo />}
        {isMobileBridgePage || isBrandPage || isClosePage || isClosePcPage ? (
          <></>
        ) : (
          <Footer isFolded={isFoldedFooter} setIsFolded={setIsFoldedFooter} scrollToBottom={scrollToBottom} />
        )}
      </div>
      <QRpopup />
    </>
  );
}

export default AppMain;
